import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import apiClient from '../api/client';
import { getAllDomainsLookup } from '../services/masterDataService';
import { Eye, EyeOff } from 'lucide-react';

const BRAND_GREEN = '#0aa375';

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '12px',
    borderColor: '#dee2e6',
    boxShadow: 'none',
    '&:hover': { borderColor: BRAND_GREEN },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
    color: state.isSelected ? 'white' : '#333',
  }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
};

const DEFAULT_ROW = { isReadOnly: true, isReadWrite: false, isActive: true };

const CreateAdminUserModal = ({ show, onClose, roles, onSuccess }) => {
  const [step, setStep] = useState('form');

  const [form, setForm] = useState({
    szUserName: '',
    szPassword: '',
    szEmail: '',
    userRoleId: null,
  });

  const [creating, setCreating] = useState(false);
  const [useDefaultPassword, setUseDefaultPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(false);

  const [createdUser, setCreatedUser] = useState(null);

  const [domains, setDomains] = useState([]);
  const [access, setAccess] = useState({});
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setStep('form');
      setForm({ szUserName: '', szPassword: '', szEmail: '', userRoleId: null });
      setCreatedUser(null);
      setDomains([]);
      setAccess({});
      setUseDefaultPassword(false);
      setShowPassword(false);
    }
  }, [show]);

  const fetchDefaultPassword = async () => {
    setLoadingDefault(true);
    try {
      const res = await apiClient.get('/api/auth/default-password');
      if (res.data.success && res.data.defaultPassword) {
        setForm(prev => ({ ...prev, szPassword: res.data.defaultPassword }));
      } else {
        toast.error("Failed to load default password");
      }
    } catch (err) {
      toast.error("Failed to load default password");
    } finally {
      setLoadingDefault(false);
    }
  };

  const handleCreate = async () => {
    if (!form.szUserName?.trim()) return toast.error("User Name is required");
    if (!form.szPassword?.trim()) return toast.error("Password is required");
    if (!form.userRoleId) return toast.error("Role is required");
    if (!form.szEmail?.trim()) return toast.error("Email is required");

    setCreating(true);
    try {
      const payload = {
        ...form,
        IsDefaultPassword: useDefaultPassword,
      };
      const res = await apiClient.post('/api/adminusers/create', payload);
      toast.success("User created successfully!");
      onSuccess?.();

      const selectedRole = roles?.find(r => r.value === form.userRoleId);
      const userId = res.data?.userId;

      setCreatedUser({ userId, roleId: form.userRoleId, roleName: selectedRole?.label || '' });

      const currentDomain = sessionStorage.getItem("selectedDomain") || "greenyourbills.com";
      setDomainsLoading(true);
      try {
        const data = await getAllDomainsLookup(currentDomain);
        setDomains(data || []);
        const initial = {};
        (data || []).forEach((d) => {
          initial[d.id] = { 
            isReadOnly: false, 
            isReadWrite: false, 
            isActive: false 
          };
        });
        setAccess(initial);
      } catch (err) {
        toast.error("Failed to load domains");
        setDomains([]);
      } finally {
        setDomainsLoading(false);
      }

      setStep('access');
    } catch (err) {
      toast.error(err.response?.data?.message || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const toggleFlag = (domainId, flag) => {
    setAccess((prev) => {
      const current = prev[domainId] || { ...DEFAULT_ROW };
      const updated = { ...current };

      if (flag === 'isActive') {
        updated.isActive = !current.isActive;
        if (updated.isActive) {
          updated.isReadOnly = true;
          updated.isReadWrite = false;
        } else {
          updated.isReadOnly = false;
          updated.isReadWrite = false;
        }
      } else if (flag === 'isReadOnly') {
        updated.isReadOnly = !current.isReadOnly;
        if (updated.isReadOnly) updated.isReadWrite = false;
      } else if (flag === 'isReadWrite') {
        updated.isReadWrite = !current.isReadWrite;
        if (updated.isReadWrite) updated.isReadOnly = false;
      }

      return { ...prev, [domainId]: updated };
    });
  };

  const handleSaveAccess = async () => {
    const rowsToSave = domains
      .filter((d) => access[d.id]?.isActive)
      .map((d) => {
        const row = access[d.id];
        return {
          domainId: d.id,
          isReadOnly: row.isReadOnly || !row.isReadWrite,
          isReadWrite: row.isReadWrite,
          status: row.isActive,
        };
      });

    if (!rowsToSave.length) return toast.error("Enable at least one domain before saving");

    setSaving(true);
    try {
      await apiClient.post('/api/userdomain/domain-access/save', {
        userId: createdUser.userId,
        roleId: createdUser.roleId,
        username: sessionStorage.getItem("username") || "System",
        domain: sessionStorage.getItem("selectedDomain") || "greenyourbills.com",
        mappings: rowsToSave,
      });
      toast.success("Domain access saved!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1060,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px'
    }} onClick={onClose}>
      
      <div style={{
        width: '100%', maxWidth: step === 'access' ? '560px' : '460px',
        background: 'white', borderRadius: '10px', overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }} onClick={e => e.stopPropagation()}>

        {/* Compact Header */}
        <div style={{ backgroundColor: BRAND_GREEN, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>
            {step === 'form' ? 'Create New User' : `Domain Access — ${createdUser?.roleName || ''}`}
          </div>
          <button type="button" style={{ fontSize: '26px', background: 'none', border: 'none', color: 'white' }} onClick={onClose}>×</button>
        </div>

        {step === 'form' && (
          <>
            <div style={{ padding: '14px' }}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="filter-label">USER NAME <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-xs"
                    value={form.szUserName}
                    onChange={(e) => setForm({ ...form, szUserName: e.target.value })}
                    autoComplete="off"
                  />
                </div>

                <div className="col-12">
                  <label className="filter-label">PASSWORD <span className="text-danger">*</span></label>
                  <div className="field-wrap" style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control form-control-xs field-input"
                      value={form.szPassword}
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setForm(prev => ({ ...prev, szPassword: newVal }));
                        if (useDefaultPassword && newVal !== form.szPassword) {
                          setUseDefaultPassword(false);
                        }
                      }}
                      autoComplete="new-password"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none' }}
                    >
                      {showPassword ? <EyeOff size={16} color="#6c757d" /> : <Eye size={16} color="#6c757d" />}
                    </button>
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-check mt-1" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="defaultPassword"
                      checked={useDefaultPassword}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setUseDefaultPassword(checked);
                        if (checked) fetchDefaultPassword();
                        else setForm(prev => ({ ...prev, szPassword: '' }));
                      }}
                      disabled={loadingDefault}
                      style={{ accentColor: BRAND_GREEN, cursor: 'pointer', marginTop: '2px' }}
                    />
                    <label className="form-check-label filter-label" htmlFor="defaultPassword" style={{ cursor: 'pointer', marginBottom: 0 }}>
                      USE DEFAULT PASSWORD {loadingDefault && '(loading...)'}
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <label className="filter-label">EMAIL <span className="text-danger">*</span></label>
                  <input
                    type="email"
                    className="form-control form-control-xs"
                    value={form.szEmail}
                    onChange={(e) => setForm({ ...form, szEmail: e.target.value })}
                    autoComplete="off"
                  />
                </div>

                <div className="col-12">
                  <label className="filter-label">ROLE <span className="text-danger">*</span></label>
                  <Select
                    options={roles || []}
                    value={roles?.find(r => r.value === form.userRoleId) || null}
                    onChange={(opt) => setForm({ ...form, userRoleId: opt?.value ?? null })}
                    placeholder="All Roles"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
            </div>

            {/* Compact Footer */}
            <div style={{ padding: '8px 14px', borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'flex-end', background: '#fafafa', gap: '8px' }}>
              <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '12px', padding: '4px 14px' }} onClick={onClose}>Cancel</button>
              <button
                className="btn btn-sm text-white"
                style={{ backgroundColor: BRAND_GREEN, fontSize: '12px', padding: '4px 18px' }}
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </>
        )}

        {step === 'access' && (
          <>
            <div style={{ padding: '14px' }}>
              {domainsLoading ? (
                <div className="text-center text-muted py-4" style={{ fontSize: '12px' }}>Loading domains...</div>
              ) : (
                <table className="table table-sm align-middle mb-0" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th className="text-center" style={{ width: '90px' }}>Is Read Only</th>
                      <th className="text-center" style={{ width: '90px' }}>Read&amp;Write</th>
                      <th className="text-center" style={{ width: '70px' }}>Is Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map((d) => {
                      const row = access[d.id] || DEFAULT_ROW;
                      return (
                        <tr key={d.id}>
                          <td>{d.text}</td>
                          <td className="text-center">
                            <input type="checkbox" checked={row.isReadOnly} disabled={!row.isActive} onChange={() => toggleFlag(d.id, 'isReadOnly')} />
                          </td>
                          <td className="text-center">
                            <input type="checkbox" checked={row.isReadWrite} disabled={!row.isActive} onChange={() => toggleFlag(d.id, 'isReadWrite')} />
                          </td>
                          <td className="text-center">
                            <input type="checkbox" checked={row.isActive} onChange={() => toggleFlag(d.id, 'isActive')} />
                          </td>
                        </tr>
                      );
                    })}
                    {!domains.length && (
                      <tr><td colSpan={4} className="text-center text-muted py-3">No domains found</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ padding: '8px 14px', borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'flex-end', background: '#fafafa' }}>
              <button className="btn btn-sm btn-outline-secondary px-3" style={{ fontSize: '12px' }} onClick={onClose}>Skip</button>
              <button
                className="btn btn-sm text-white px-4 ms-2"
                style={{ backgroundColor: BRAND_GREEN, fontSize: '12px' }}
                onClick={handleSaveAccess}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateAdminUserModal;